import * as THREE from 'three'
import { toXZ } from '../kanjinData'

interface LinesJson {
  rings: [number, number][][]
}

/** 日本列島（海岸線ライン＋白木ジオラマ面）・海面・雲海 */
export class JapanTerrain {
  readonly root = new THREE.Group()
  readonly clouds = new THREE.Group()
  private lineMat!: THREE.LineBasicMaterial
  private landGroup = new THREE.Group()
  private cloudMats: THREE.MeshBasicMaterial[] = []
  private time = 0

  async load(url: string): Promise<void> {
    const data: LinesJson = await fetch(url).then((r) => r.json())

    // --- 海面 ---
    const sea = new THREE.Mesh(
      new THREE.PlaneGeometry(360, 360),
      new THREE.MeshBasicMaterial({ color: 0x0e1a2a, transparent: true, opacity: 0.85 }),
    )
    sea.rotation.x = -Math.PI / 2
    sea.position.y = -0.4
    this.root.add(sea)

    // --- 海岸線ライン（全リングを1本のLineSegmentsへ） ---
    const pos: number[] = []
    for (const ring of data.rings) {
      for (let i = 0; i < ring.length; i++) {
        const a = ring[i]
        const b = ring[(i + 1) % ring.length]
        const pa = toXZ(a[1], a[0])
        const pb = toXZ(b[1], b[0])
        pos.push(pa.x, 0.12, pa.z, pb.x, 0.12, pb.z)
      }
    }
    const lineGeo = new THREE.BufferGeometry()
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
    this.lineMat = new THREE.LineBasicMaterial({ color: 0x9fc3e0, transparent: true, opacity: 0.55 })
    this.root.add(new THREE.LineSegments(lineGeo, this.lineMat))

    // --- 白木の陸地（大きいリング上位のみ ShapeGeometry で面化） ---
    const woodTex = JapanTerrain.makeWoodTexture()
    const landMat = new THREE.MeshStandardMaterial({
      map: woodTex,
      color: 0xcbb890,
      roughness: 0.85,
      metalness: 0,
    })
    const big = [...data.rings].sort((a, b) => b.length - a.length).slice(0, 14)
    for (const ring of big) {
      const shape = new THREE.Shape()
      ring.forEach(([lon, lat], i) => {
        const p = toXZ(lat, lon)
        if (i === 0) shape.moveTo(p.x, -p.z)
        else shape.lineTo(p.x, -p.z)
      })
      const geo = new THREE.ShapeGeometry(shape)
      geo.rotateX(-Math.PI / 2) // (x, y) -> (x, 0, -y) なので shape.y に -z を入れてある
      const mesh = new THREE.Mesh(geo, landMat)
      mesh.position.y = 0.05
      this.landGroup.add(mesh)
    }
    this.root.add(this.landGroup)

    // --- 雲海（2層のドリフトするアルファ板。既定は非表示） ---
    const cloudTex = JapanTerrain.makeCloudTexture()
    for (const [y, scale, op] of [
      [3.0, 1.0, 0.85],
      [4.6, 1.6, 0.5],
    ] as const) {
      const mat = new THREE.MeshBasicMaterial({
        map: cloudTex,
        transparent: true,
        opacity: op,
        depthWrite: false,
      })
      const plane = new THREE.Mesh(new THREE.PlaneGeometry(420 * scale, 420 * scale), mat)
      plane.rotation.x = -Math.PI / 2
      plane.position.y = y
      this.clouds.add(plane)
      this.cloudMats.push(mat)
    }
    this.clouds.visible = false
    this.root.add(this.clouds)
  }

  /** 陸地の高さ（雲海突き破り演出用） */
  setLandY(y: number): void {
    this.landGroup.position.y = y
  }

  setLineOpacity(o: number): void {
    if (this.lineMat) this.lineMat.opacity = o
  }

  setCloudsVisible(v: boolean): void {
    this.clouds.visible = v
  }

  /** main のリセットで呼ぶ：位置・スケール・表示状態を既定へ */
  resetTransforms(): void {
    this.root.position.set(0, 0, 0)
    this.root.scale.setScalar(1)
    this.setLandY(0)
    this.setLineOpacity(0.55)
    this.setCloudsVisible(false)
  }

  update(dt: number): void {
    this.time += dt
    this.cloudMats.forEach((m, i) => {
      if (!m.map) return
      m.map.offset.x = this.time * 0.004 * (i + 1)
      m.map.offset.y = this.time * 0.002 * (i ? -1 : 1)
    })
  }

  private static makeWoodTexture(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 512
    const ctx = c.getContext('2d')!
    ctx.fillStyle = '#c9b183'
    ctx.fillRect(0, 0, 512, 512)
    for (let i = 0; i < 90; i++) {
      const y = Math.random() * 512
      ctx.strokeStyle = `rgba(120,90,50,${0.05 + Math.random() * 0.12})`
      ctx.lineWidth = 1 + Math.random() * 2
      ctx.beginPath()
      ctx.moveTo(0, y)
      for (let x = 0; x <= 512; x += 32) ctx.lineTo(x, y + Math.sin(x * 0.02 + i) * 6)
      ctx.stroke()
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.repeat.set(6, 6)
    return tex
  }

  private static makeCloudTexture(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 512
    const ctx = c.getContext('2d')!
    ctx.clearRect(0, 0, 512, 512)
    for (let i = 0; i < 260; i++) {
      const x = Math.random() * 512
      const y = Math.random() * 512
      const r = 22 + Math.random() * 60
      const g = ctx.createRadialGradient(x, y, 0, x, y, r)
      const a = 0.05 + Math.random() * 0.1
      g.addColorStop(0, `rgba(235,238,245,${a})`)
      g.addColorStop(1, 'rgba(235,238,245,0)')
      ctx.fillStyle = g
      ctx.fillRect(x - r, y - r, r * 2, r * 2)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    return tex
  }
}
